// app/components/HeroBanner.tsx
'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';

interface BannerSlide {
  id: number;
  image: string;
  gradient?: string;
}

const AUTOPLAY_MS = 4000;
const FALLBACK_CONTAINER_WIDTH = 360; // used during SSR before client width is known

const HeroBanner = () => {
  // ✅ slides sekarang stabil (referensinya tidak berubah di tiap render)
  const slides = useMemo<BannerSlide[]>(
    () => [
      { id: 1, image: '/assets/img/banner/slide1.jpeg', gradient: 'from-black/25 via-transparent to-black/25' },
      { id: 2, image: '/assets/img/banner/slide2.jpeg', gradient: 'from-black/25 via-transparent to-black/25' },
      { id: 3, image: '/assets/img/banner/slide3.jpeg', gradient: 'from-black/25 via-transparent to-black/25' }
    ],
    []
  );

  // clone untuk loop mulus
  const extendedSlides = useMemo(
    () => [slides[slides.length - 1], ...slides, slides[0]],
    [slides]
  );

  const [currentIndex, setCurrentIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Fungsi untuk menentukan greeting berdasarkan waktu
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 11) return 'Selamat pagi';
    if (hour < 15) return 'Selamat siang';
    if (hour < 18) return 'Selamat sore';
    return 'Selamat malam';
  };

  // Touch handlers untuk swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrev();
    }
  };

  useEffect(() => {
    if (!isAutoPlay) return;
    const t = setInterval(() => {
      setCurrentIndex((p) => p + 1);
      setIsTransitioning(true);
    }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [isAutoPlay]);

  const handleTransitionEnd = () => {
    if (currentIndex === 0) {
      setIsTransitioning(false);
      setCurrentIndex(slides.length);
    } else if (currentIndex === slides.length + 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      const id = requestAnimationFrame(() => setIsTransitioning(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isTransitioning]);

  // sync container width so per-slide width follows the actual visible area (not the viewport)
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const goToPrev = () => {
    setCurrentIndex((p) => p - 1);
    setIsTransitioning(true);
  };
  const goToNext = () => {
    setCurrentIndex((p) => p + 1);
    setIsTransitioning(true);
  };

  // dots aktif (index asli)
  const activeDot =
    currentIndex === 0
      ? slides.length - 1
      : currentIndex === slides.length + 1
      ? 0
      : currentIndex - 1;

  const goToRealIndex = (i: number) => {
    setCurrentIndex(i + 1);
    setIsTransitioning(true);
  };

  const effectiveContainerWidth = containerWidth || FALLBACK_CONTAINER_WIDTH;
  const itemWidthPx = effectiveContainerWidth * 0.9; // sedikit peek left/right
  const trackWidthPx = extendedSlides.length * itemWidthPx;
  const translateCenterPx = currentIndex * itemWidthPx - (effectiveContainerWidth - itemWidthPx) / 2;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle search logic here
    console.log('Search query:', searchQuery);
  };

  return (
    <section className="relative bg-gradient-to-b from-orange-400 to-white">
      {/* Search Focus Overlay */}
      {isSearchFocused && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-all duration-300"
          onClick={() => setIsSearchFocused(false)}
        />
      )}

      {/* Greeting Section */}
      <div className="px-6 sm:px-6 md:px-8 pt-4 pb-3">
        <p className="text-base sm:text-lg md:text-xl text-white/90 text-left font-poppins">
          {getGreeting()},
        </p>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white text-left font-poppins">
          orang baik
        </h1>
      </div>

      {/* Search Section */}
      <div className="px-6 sm:px-6 md:px-8 relative z-50">
        <form onSubmit={handleSearch} className="w-full max-w-2xl mx-auto">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              placeholder="Cari apa yang Anda butuhkan..."
              className={`
                w-full px-4 py-3 sm:px-6 sm:py-4 pr-12 text-gray-700 bg-white rounded-full border-0 shadow-lg 
                focus:outline-none focus:ring-2 focus:ring-orange-300 focus:shadow-xl 
                transition-all duration-200
                ${isSearchFocused ? 'scale-105 shadow-2xl' : ''}
              `}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 sm:h-10 sm:w-10 bg-orange-400 hover:bg-orange-500 rounded-full flex items-center justify-center transition-colors duration-200"
              aria-label="Cari"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 sm:h-6 sm:w-6 text-white">
                <path
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>
      </div>

      {/* Banner Section */}
      <div
        className="relative overflow-hidden"
        style={{ height: 'clamp(230px, 40vw, 230px)' }}
        ref={containerRef}
      >
        <div
          className="relative h-full w-full"
          onMouseEnter={() => setIsAutoPlay(false)}
          onMouseLeave={() => setIsAutoPlay(true)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Track */}
          <div
            className="flex h-full items-center"
            style={{
              width: `${trackWidthPx}px`,
              transform: `translateX(-${translateCenterPx}px)`,
              transition: isTransitioning ? 'transform 700ms ease' : 'none',
            }}
            onTransitionEnd={handleTransitionEnd}
          >
            {extendedSlides.map((slide, idx) => {
              const isActive = idx === currentIndex;
              return (
                <div
                  key={`${slide.id}-${idx}`}
                  className="relative flex-shrink-0 h-[85%] sm:px-1 flex items-center justify-center"
                  style={{ width: `${itemWidthPx}px` }}
                  aria-hidden={!isActive}
                >
                  <div
                    className={`
                      relative w-full h-full rounded-3xl overflow-hidden 
                      transition-all duration-500
                      ${isActive ? 'scale-100' : 'scale-[0.95]'}
                    `}
                  >
                    <Image
                      src={slide.image}
                      alt=""
                      width={354}
                      height={196}
                      className={`
                        absolute inset-0 w-full h-full object-cover
                        transition-all duration-500
                        ${isActive ? 'blur-0 opacity-100' : 'opacity-100'}
                      `}
                    />
                    
                    <div className="absolute inset-0 rounded-3xl ring-1 ring-black/10" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dots */}
          <div className="absolute bottom-0 sm:bottom-5 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            {slides.map((_, i) => {
              const isActive = i === activeDot;
              return (
                <button
                  key={i}
                  onClick={() => goToRealIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`
                    h-2 sm:h-2.5
                    transition-all duration-300 ease-out
                    ${isActive ? 'w-10 sm:w-8 bg-orange-400' : 'w-2 sm:w-2.5 bg-gray-300/60 hover:bg-gray-500'}
                    rounded-full
                  `}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
